import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faDisplay } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MarkerDot, SquiggleItem, SquiggleTypes } from '../types/types';
import './ImageDialog.css';
import ImageShowcase from './ImageShowcase';
import ItemSelector from '../shared/ItemSelector';
import { useAdventureDataContext } from '../context/AdventureDataContext';
import backend from '../backend';

type MarkerType = SquiggleTypes | 'marker' | 'selected';

const textColors = {
  quest: '#c9f1d6',
  personororg: '#f8ebd3',
  place: '#f1ddda',
  miscitem: '#dde7ee',
  logentry: '#f5f5f5',
  system: '#000',
  "marker": '#d6c9f1',
  "selected": '#fff'
};

const circleColors = {
  quest: '#284',
  personororg: '#d92',
  place: '#b54',
  miscitem: '#58a',
  logentry: '#ccc',
  system: '#000',
  marker: '#85a',
  "selected": '#fff'
};

const sizeParams = {
  small: { radius: 4.5, fontSize: 12, circleStrokeWidth: 1.5, textStrokeWidthBackground: 1.2, textStrokeWidthForeground: 0.3, yOffset: 3, xOffset: 7.5 },
  medium: { radius: 7.5, fontSize: 16.5, circleStrokeWidth: 3, textStrokeWidthBackground: 2.25, textStrokeWidthForeground: 0.75, yOffset: 4.5, xOffset: 12 },
  large: { radius: 10, fontSize: 24, circleStrokeWidth: 4, textStrokeWidthBackground: 3, textStrokeWidthForeground: 1.2, yOffset: 7.5, xOffset: 16.5 }
}

interface ResolvedMarker {
  markerId: string;
  type: SquiggleTypes|'marker';
  name: string;
  position: { x: number, y: number };
  link: SquiggleItem|undefined;
  dot: MarkerDot;
}

interface ShowMarker {
  markerId?: string;
  type: MarkerType;
  name: string;
  position: { x: number, y: number };
  link?: SquiggleItem;
  dot: MarkerDot;
}

function ImageDialog({
  item,
  imageIndex,
  imageSeed,
  openItem,
  setSelectedItemId,
  close,
  addMarker,
  removeMarker
} : {
  item: SquiggleItem,
  imageIndex: number,
  imageSeed: number,
  openItem: (itemId: string) => void,
  setSelectedItemId: (itemId: string) => void,
  close: () => void,
  addMarker: (itemId: string, imageIndex: number, toItemId: string|undefined, name: string, x: number, y: number, dot: MarkerDot) => void,
  removeMarker: (itemId: string, imageIndex: number, markerId: string) => void
}) {
  const { allUserItems, itemById } = useAdventureDataContext();

  const markers: ResolvedMarker[] = useMemo(() =>
    Object.entries(item.images[imageIndex].markers)
      .map(([markerId, marker]) => ({ id: markerId, link: itemById.get(marker.link ?? ""), marker: marker }))
      .map(l => ({
        markerId: l.id,
        name: l.marker.name || l.link?.name || "",
        type: (l.link?.type || 'marker') as SquiggleTypes|'marker',
        position: l.marker,
        link: l.link,
        dot: l.marker.dot
      }))
  , [item, itemById, imageIndex]);

  const potentialLinks = useMemo(() =>
    allUserItems.filter(i => i.id !== item.id && !i.archived),
    [item, allUserItems]
  );

  const [markerBeingAdded, setMarkerBeingAdded] = useState<{ x: number, y: number, link?: string, name?: string, overrideName?: boolean, dot?: MarkerDot }>();
  const [selectedMarkerId, setSelectedMarkerId] = useState<string>();
  const [imageAspectRatio, setImageAspectRatio] = useState<number>(1);
  const [svgScale, setSvgScale] = useState<number>(1);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [markerSize, setMarkerSize] = useState<keyof typeof sizeParams>("medium");
  const [keepOpen, setKeepOpen] = useState<boolean>(false);
  const [hideMarkers, setHideMarkers] = useState<boolean>(false);
  const [showcase, setShowcase] = useState<boolean>(false);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const resizeImage = () => {
      const container = containerRef.current;
      const img = imgRef.current;
      const svg = svgRef.current;

      if (!container || !img || !svg) {
        return;
      }

      const containerAspectRatio = container.offsetWidth / container.offsetHeight;
      const imageAspectRatio = img.naturalWidth / img.naturalHeight;

      if (imageAspectRatio > containerAspectRatio) {
        img.style.width = '100%';
        img.style.height = `calc(90vw / ${imageAspectRatio})`;
        img.style.left = '0';
        img.style.top = `calc((90vh - (90vw / ${imageAspectRatio})) / 2)`
        svg.style.width = '100%'
        svg.style.height = `calc(90vw / ${imageAspectRatio})`;
        svg.style.left = '0';
        svg.style.top = `calc((90vh - (90vw / ${imageAspectRatio})) / 2)`
        setSvgScale(imageAspectRatio * 100 / container.offsetWidth);
      } else {
        img.style.width = `calc(90vh * ${imageAspectRatio})`;
        img.style.height = '100%'
        img.style.left = `calc((90vw - (90vh * ${imageAspectRatio})) / 2)`;
        img.style.top = '0';
        svg.style.width = `calc(90vh * ${imageAspectRatio})`;
        svg.style.height = '100%'
        svg.style.left = `calc((90vw - (90vh * ${imageAspectRatio})) / 2)`;
        svg.style.top = '0';
        setSvgScale(100 / container.offsetHeight);
      }

      setImageAspectRatio(imageAspectRatio);
    };

    // Resize image on load
    const img = imgRef.current;
    if (img?.complete) {
      resizeImage();
    } else {
      img!.onload = resizeImage;
    }

    // Resize image on window resize
    window.addEventListener('resize', resizeImage);
    return () => {
      window.removeEventListener('resize', resizeImage);
    };
  }, []);

  useEffect(() => {
    if (!markerBeingAdded && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [markerBeingAdded]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      if (isEditing) {
        setIsEditing(false);
        setMarkerBeingAdded(undefined);
        setSelectedMarkerId(undefined);
      } else {
        close();
      }
      e.preventDefault();
    }

    // Always stop propagation so global window shortcuts don't fire
    e.stopPropagation();
  };

  const setupNewLink = (e: MouseEvent<SVGSVGElement>) => {
    if (!isEditing) {
      return;
    }

    const bounds = e.currentTarget.getBoundingClientRect();
    setMarkerBeingAdded({ x: (e.clientX - bounds.x)/bounds.height*100, y: (e.clientY - bounds.y)/bounds.height*100, dot: "auto" });
    setSelectedMarkerId(undefined);
  };

  const finishNewMarker = () => {
    addMarker(
      item.id,
      imageIndex,
      markerBeingAdded!.link,
      markerBeingAdded!.name!,
      markerBeingAdded!.x,
      markerBeingAdded!.y,
      markerBeingAdded!.dot!);
    setMarkerBeingAdded(undefined);
  };

  const markerClick = (e: MouseEvent, markerId: string) => {
    if (isEditing) {
      setSelectedMarkerId(markerId);
      setMarkerBeingAdded(undefined);
    } else if (markerId) {
      const marker = showMarkers.find(m => m.markerId === markerId);
      if (marker!.link) {
        openItem(marker!.link.id);
        if (!keepOpen) {
          close();
          setSelectedItemId(marker!.link.id);
        }
      }
      
    }
    e.stopPropagation();
  };

  const deleteAll = () => {
    if (window.confirm("Are you sure you want to delete all markers from this image?")) {
      for (const marker of markers) {
        removeMarker(item.id, imageIndex, marker.markerId);
      }
    }
  };

  const showMarkers: ShowMarker[] = [...markers];
  if (markerBeingAdded) {
    showMarkers.push({
      type: 'selected',
      name: markerBeingAdded.name ? markerBeingAdded.name :  itemById.get(markerBeingAdded.link ?? "")?.name ?? 'NEW LINK',
      position: markerBeingAdded,
      dot: markerBeingAdded.dot!
    });
  }

  const hideShowCase = useCallback(() => {
    setShowcase(false);
  }, [setShowcase]);

  return (
    <div
      className="imagedialog"
      tabIndex={-1}
      ref={dialogRef}
      onKeyDown={handleKeyDown}
    >
      <div className="toolbar">
        { document.fullscreenEnabled && <button onClick={() => setShowcase(true)} title="Showcase fullscreen"><FontAwesomeIcon icon={faDisplay as IconProp} /></button> }
        { showcase && <ImageShowcase imageUrl={backend.getImageUrl(item.images[imageIndex].imageFileName!, imageSeed)} close={hideShowCase} /> }
        { selectedMarkerId && <button onClick={() => { removeMarker(item.id, imageIndex, selectedMarkerId); setSelectedMarkerId(undefined); }}>Delete selected</button> }
        { isEditing && <button className="deleteall" onClick={deleteAll}>Delete ALL</button> }
        { isEditing ? <button onClick={() => { setIsEditing(false); setMarkerBeingAdded(undefined); setSelectedMarkerId(undefined); }}>Done editing</button> : <button onClick={() => setIsEditing(true)}>Edit markers</button> }
        <div>
          <label htmlFor="markersize" style={{ paddingBottom: "2px" }}>Marker size:&nbsp;</label>
          <select id="markersize" value={markerSize} onChange={(e) => setMarkerSize(e.target.value as keyof typeof sizeParams)}>
            <option key="small" value="small">Small</option>
            <option key="medium" value="medium">Medium</option>
            <option key="large" value="large">Large</option>
          </select>
        </div>
        <div>
          <input type="checkbox" id="keepopen" value={keepOpen ? "checked" : ""} onChange={(e) => setKeepOpen(!keepOpen)}></input>
          <label htmlFor="keepopen" style={{ cursor: "pointer" }}>Keep open</label>
        </div>
        <div>
          <input type="checkbox" id="hidemarkers" value={hideMarkers ? "checked" : ""} onChange={(e) => setHideMarkers(!hideMarkers)}></input>
          <label htmlFor="hidemarkers" style={{ cursor: "pointer" }}>Hide markers</label>
        </div>
        <button onClick={close}>Close</button>
      </div>
      <div className={"image " + (isEditing ? "editing" : "")} ref={containerRef}>
        <img alt="Canvas for link pinning" src={backend.getImageUrl(item.images[imageIndex].imageFileName!, imageSeed)} ref={imgRef}></img>
        <svg className="itempins" viewBox={`0 0 ${100*imageAspectRatio} 100`} ref={svgRef} onClick={setupNewLink}>
        {
          !hideMarkers && showMarkers.map(m =>
            <Fragment key={m.markerId ?? "new"}>
              { m.dot !== "none" && <circle key={"c" + m.markerId} className={isEditing || m.link ? "clickable":""} onClick={(e) => m.markerId && markerClick(e, m.markerId)} r={sizeParams[markerSize].radius*svgScale} cx={m.position.x} cy={m.position.y} fill={circleColors[selectedMarkerId === m.markerId ? "selected" : m.type]} stroke="black" strokeWidth={sizeParams[markerSize].circleStrokeWidth*svgScale} opacity="0.8"/> }
              <text
                key={"s" + m.markerId}
                className="shadow"
                x={m.position.x + sizeParams[markerSize].xOffset*svgScale * (m.dot === "none" ? 0 : (m.dot === "auto" ? (m.position.x < 50*imageAspectRatio ? 1 : -1) : (m.dot === "left" ? 1 : -1)))}
                y={m.position.y + sizeParams[markerSize].yOffset*svgScale}
                fontSize={sizeParams[markerSize].fontSize*svgScale + "px"}
                fontWeight="bold"
                textAnchor={m.dot === "none" ? "middle" : (m.dot === "auto" ? (m.position.x < 50*imageAspectRatio ? "start" : "end") : (m.dot === "left" ? "start" : "end"))}
                fill="black"
                stroke="black"
                strokeWidth={sizeParams[markerSize].textStrokeWidthBackground*svgScale}>
                  {m.name}
              </text>
              <text
                key={"t" + m.markerId}
                className={isEditing || m.link ? "clickable":""}
                onClick={(e) => m.markerId && markerClick(e, m.markerId)}
                x={m.position.x + sizeParams[markerSize].xOffset*svgScale * (m.dot === "none" ? 0 : (m.dot === "auto" ? (m.position.x < 50*imageAspectRatio ? 1 : -1) : (m.dot === "left" ? 1 : -1)))}
                y={m.position.y + sizeParams[markerSize].yOffset*svgScale}
                fontSize={sizeParams[markerSize].fontSize*svgScale + "px"}
                fontWeight="bold"
                textAnchor={m.dot === "none" ? "middle" : (m.dot === "auto" ? (m.position.x < 50*imageAspectRatio ? "start" : "end") : (m.dot === "left" ? "start" : "end"))}
                fill={textColors[selectedMarkerId === m.markerId ? "selected" : m.type]}
                stroke="#000"
                strokeWidth={sizeParams[markerSize].textStrokeWidthForeground*svgScale}>
                  {m.name}
              </text>
            </Fragment>
          )
        }
        </svg>
      </div>
      { markerBeingAdded &&
        <div className="addmarkerdialog">
          <div>
            <label htmlFor="newlink">Item to link to:</label>
            <ItemSelector id='newlink' availableItems={potentialLinks} selectedItemId={markerBeingAdded.link ?? ""} onChange={(itemId) => setMarkerBeingAdded({ ...markerBeingAdded, link: itemId })} noneText="NO LINK" emptyParentName="Unavailable parent" sizeAfterContent />
          </div>
          {
            markerBeingAdded.link && <div>
              <div>
                <input type="checkbox" id="overridename" value={markerBeingAdded.overrideName ? "checked" : ""} onChange={(e) => setMarkerBeingAdded({ ...markerBeingAdded, overrideName: e.target.checked })} />
                <label htmlFor="overridename">Override name</label>
              </div>
            </div>
          }
          {
            (!markerBeingAdded.link || markerBeingAdded.overrideName) && <div>
              <label htmlFor="newname">Marker name*</label>
              <input type="text" id="newname" placeholder="Required" autoFocus value={markerBeingAdded.name||''} onChange={(e) => setMarkerBeingAdded({ ...markerBeingAdded, name: e.target.value })} />
            </div>
          }
          <div>
            <label htmlFor="dot">Dot:</label>
            <select name="dot" value={markerBeingAdded.dot!} onChange={(e) => setMarkerBeingAdded({ ...markerBeingAdded, dot: e.target.value as MarkerDot})}>
              <option value="auto">Auto</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
              <option value="none">None</option>
            </select>
          </div>
          <div className="addmarkerbuttons">
            <button onClick={() => (markerBeingAdded.link || markerBeingAdded.name) && finishNewMarker()} disabled={(!markerBeingAdded.link && !markerBeingAdded.name) || (markerBeingAdded.overrideName && !markerBeingAdded.name)}>Add marker</button>
            <button onClick={() => setMarkerBeingAdded(undefined)}>Cancel</button>
          </div>
        </div>
      }
    </div>
  );
}

export default ImageDialog;